import type { ReportVersionDto } from '@/api/WhisApi/generated';
import { Button } from '@/components/Button/Button';
import { Grabber } from '@/components/Grabber';
import { LoadingView } from '@/components/LoadingView/LoadingView';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useSafeAreaPadding } from '@/hooks/useSafeAreaPadding';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView } from 'react-native';
import { Sheet, SizableText, useTheme, XStack, YStack } from 'tamagui';
import { ReportVersionCard } from './ReportVersionCard';

interface ReportVersionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: ReportVersionDto[];
  onSelectVersion?: (version: ReportVersionDto) => void;
  selectedVersionId?: string;
  isLoading?: boolean;
}

export const ReportVersionsSheet = ({
  open,
  onOpenChange,
  versions,
  onSelectVersion,
  selectedVersionId,
  isLoading = false,
}: ReportVersionsSheetProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const padding = useSafeAreaPadding();
  const theme = useTheme();

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[90]}
      snapPointsMode="percent"
      modal
      dismissOnSnapToBottom
    >
      <Sheet.Frame
        paddingTop="$true"
        paddingBottom={padding.bottomPadding}
        borderRadius="$modal"
        flex={1}
      >
        <YStack flex={1} backgroundColor="$background" gap="$4">
          <Grabber />
          <XStack
            paddingHorizontal="$true"
            gap="$2"
            alignItems="center"
            justifyContent="space-between"
          >
            <SizableText size="$h6" fontFamily="$bold">
              {t('reportDetail.versions.title')}
            </SizableText>
            <Button
              variant="ghost"
              icon={<Ionicons name="close" size={24} color={theme.color.val} />}
              onPress={() => onOpenChange(false)}
              maxWidth={30}
              padding={0}
            />
          </XStack>
          {isLoading ? (
            <LoadingView />
          ) : (
            <ScrollView keyboardShouldPersistTaps="always" nestedScrollEnabled>
              <YStack paddingHorizontal="$true" paddingBottom="$true" gap="$3">
                {versions.map((version) => (
                  <ReportVersionCard
                    key={`${version.id}-${version.versionNumber}`}
                    version={version}
                    isSelected={version.id === selectedVersionId}
                    onPress={
                      onSelectVersion
                        ? (): void => onSelectVersion(version)
                        : undefined
                    }
                  />
                ))}
              </YStack>
            </ScrollView>
          )}
        </YStack>
      </Sheet.Frame>
      <Sheet.Overlay backgroundColor="$shadow" />
    </Sheet>
  );
};
